import React from 'react';
import { Button, Card, CardActions, CardContent } from '@material-ui/core';
import LinesEllipsis from 'react-lines-ellipsis';
import DOMPurify from 'dompurify';
import { useHistory } from 'react-router-dom';

import OrganizationChip from 'components/OrganizationChip';
import { capitalizeFirstLetter } from 'utils/stringFormat';

import { ActionTemplate } from '../types';

import { ActionTemplateCardImage } from './ActionTemplateCardImage';
import { CreateActionFromTemplateButton } from './CreateActionFromTemplateButton';
import { RewardChip } from './RewardChip';

const extractAndTruncateTextFromHTML = (htmlString: string, maxLength = 100) => {
  const sanitizedHTML = DOMPurify.sanitize(htmlString);

  const tempElement = document.createElement('div');
  tempElement.innerHTML = sanitizedHTML;

  const text = tempElement.textContent || '';

  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

const SubText = ({ actionTemplate }: { actionTemplate: ActionTemplate }) => {
  const subText = actionTemplate.subTitle || actionTemplate.description;

  if (!subText) {
    return null;
  }

  const formattedText = extractAndTruncateTextFromHTML(subText);

  return (
    <LinesEllipsis
      text={formattedText}
      maxLine={2}
      component="p"
      style={{
        height: 20,
        fontFamily: 'Graphik',
        margin: '0 0 28px 0',
        fontSize: 12,
      }}
    />
  );
};

export const ActionTemplateCard = ({ actionTemplate }: { actionTemplate: ActionTemplate }) => {
  const history = useHistory();

  return (
    <>
      <Card
        style={{
          position: 'relative',
          borderRadius: 15,
          borderWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ position: 'relative' }}>
          <ActionTemplateCardImage actionTemplate={actionTemplate} />
          <RewardChip actionTemplate={actionTemplate} isAbsolute />
        </div>
        <CardContent style={{ padding: '16px 16px 8px 16px', flexGrow: 1 }}>
          <LinesEllipsis
            text={actionTemplate.title}
            maxLine={2}
            component="h4"
            style={{
              height: 48,
              fontFamily: 'Graphik',
              margin: '0 0 0 0',
              fontSize: 18,
              fontWeight: 500,
            }}
          />

          <SubText actionTemplate={actionTemplate} />
          <OrganizationChip
            size="small"
            organization={{
              name: capitalizeFirstLetter(actionTemplate.status),
            }}
          />
        </CardContent>
        <CardActions style={{ padding: '0 16px', marginBottom: 8, alignItems: 'flex-end' }}>
          <Button
            color="primary"
            variant="outlined"
            onClick={() => {
              history.push(`/actions/template/${actionTemplate.id}`);
            }}
            style={{ margin: '0 0 8px 8px' }}
          >
            Details
          </Button>
          <CreateActionFromTemplateButton actionTemplate={actionTemplate} />
        </CardActions>
      </Card>
    </>
  );
};
